import {View} from '@fantasmagames/slot/src/common/view/View';
import {SpineAnimation} from '@fantasmagames/core/src/display/SpineAnimation';
import {WinNumbers} from '@fantasmagames/slot/src/modules/winpresentation/WinNumbers';
import {Position, SymbolPosition} from '@fantasmagames/slot/src/modules/reels/ReelConstants';
import {GameReelConstants} from 'src/reels/GameReelConstants';
import {Tween} from '@fantasmagames/core/src/tween/Tween';
import {Easing} from '@fantasmagames/core/src/tween/Easing';
import {TimingConstants} from 'src/timing/TimingConstants';
import {BackgroundView} from '@fantasmagames/slot/src/modules/background/BackgroundView';
import {SpineAnimationMultiTrack} from '@fantasmagames/core/src/display/SpineAnimationMultiTrack';

export interface WildCoinOnReels {
	symbolPosition: SymbolPosition;
	animation: SpineAnimation;
}

export class WildMeterView extends View {
	public static readonly WIN_METER_WILDS_ANIMATIONS_FINISHED: string = 'WildMeterView::WIN_METER_WILDS_ANIMATIONS_FINISHED';
	public static readonly WIN_METER_EXIT_WIN_ANIMATION_STARTED: string = 'WildMeterView::WIN_METER_EXIT_WIN_ANIMATION_STARTED';
	public static readonly WIN_METER_EXIT_WIN_ANIMATION_FINISHED: string = 'WildMeterView::WIN_METER_EXIT_WIN_ANIMATION_FINISHED';
	public static readonly ADD_WILD_COIN_TO_REELS: string = 'WildMeterView::ADD_WILD_COIN_TO_REELS';
	public static readonly SEND_COIN_TO_METER: string = 'WildMeterView::SEND_COIN_TO_METER';
	public static readonly COIN_HIT_METER: string = 'WildMeterView::COIN_HIT_METER';
	public static readonly MULTIPLIER_UDPATE: string = 'WildMeterView::MULTIPLIER_UDPATE';
	public static readonly INDICATOR_LIT: string = 'WildMeterView::INDICATOR_LIT';

	public static readonly MAX_IDLE_LVL: number = 5;
	public static readonly MP_LVL_TO_UPGRADE_IDLE: number[] = [3, 5, 7, 9];

	protected _currentCoinsOnMeter: number = 0;

	protected _meter: SpineAnimationMultiTrack;
	protected _multiplier: SpineAnimation;
	protected _flyingWin: SpineAnimation;
	protected _currentMultiplierValue: number = 1;

	protected _meterLayer: PIXI.Container;
	protected _multiplierNumberLayer: PIXI.Container;

	protected _wildCoinsOnReels: WildCoinOnReels[] = [];
	protected _currentCoinFlyingIndex: number = 0;

	private _isInFreeSpins: boolean = false;

	public init(): void {
		super.init();

		this._meterLayer = new PIXI.Container();
		this.container.addChild(this._meterLayer);
		this._multiplierNumberLayer = new PIXI.Container();
		this.container.addChild(this._multiplierNumberLayer);

		this._meter = this.spineUtils.startSpineAnimationCreation('WILD_MULTIPLIER_METER.json', 'STAGE_00_IDLE', true)
			.position(this.layout.getAbsolutePosition('MULTIPLIER_METER'))
			.container(this._meterLayer)
			.createMultitrack();

		this.playMultiplierIdleAnimation();

		this._multiplier = this.spineUtils.startSpineAnimationCreation('WILD_MULTIPLIER.json', 'IDLE', true)
			.position(this.layout.getAbsolutePosition('MULTIPLIER_METER'))
			.container(this._multiplierNumberLayer)
			.createAndStart();

		this.setMultiplierValue(1);
	}

	public playMultiplierIdleAnimation(lvl: number = 0): void {
		this._meter.play(`IDLE_0${lvl}`, 1, true);
	}

	public setMultiplierValue(value: number, forNewNumber: boolean = false, isFlyingWin: boolean = false): void {
		const multiplierNumber: WinNumbers = new WinNumbers(this.resources, value === 1 ? 'MULTIPLIER_SILVER_' : 'MULTIPLIER_');
		multiplierNumber.numberAnchor = {x: 0, y: 0.5};

		multiplierNumber.autoCenterMyself = true;
		multiplierNumber.additionalOffset = -30;
		multiplierNumber.numberKerning = -15;
		multiplierNumber.showNumber(`x${value.toString()}`);

		const multiplier: SpineAnimation = isFlyingWin ? this._flyingWin : this._multiplier;
		this.spineUtils.replaceElementInSpineAnimation(multiplier, forNewNumber ? 'MULTIPLIER_NUMBER_NEW' : 'MULTIPLIER_NUMBER_OLD', multiplierNumber);
		multiplierNumber.position.x = -30;
	}

	public playMultiplierTransition(fromValue: number, toValue: number, isSecond: boolean = false, onCompleted: Function): void {
		this.killMultiplier();

		this.checkIfMultiplierIdleShouldBeUpdated(toValue);

		this._multiplier = this.spineUtils.startSpineAnimationCreation('WILD_MULTIPLIER.json', 'TRANSITION', false)
			.position(this.layout.getAbsolutePosition('MULTIPLIER_METER'))
			.chainIdleAnimation('IDLE')
			.container(this._multiplierNumberLayer)
			.onComplete(() => {
				this.setMultiplierValue(toValue);
				onCompleted && onCompleted();
			})
			.createAndStart();

		isSecond && this._multiplier.gotoAnimationFrame(15);

		this.setMultiplierValue(fromValue);
		this.setMultiplierValue(toValue, true);
	}

	public checkIfMultiplierIdleShouldBeUpdated(toValue: number): void {
		if (WildMeterView.MP_LVL_TO_UPGRADE_IDLE.indexOf(toValue) === -1) {
			return;
		}

		this.updateMultiplierIdleAnimation(toValue);
	}

	public updateMultiplierIdleAnimation(toValue: number): void {
		const to: number = (Math.min(Math.round(toValue / 2), WildMeterView.MAX_IDLE_LVL));
		const idle: number = (Math.min(Math.round(toValue / 2), WildMeterView.MAX_IDLE_LVL));

		this._meter.play(`UPPDATE_0${to - 1}_TO_0${to}`, 1, false)
			.setTrackOnCompleteCallback(1, () =>
				this.playMultiplierIdleAnimation(idle));
	}

	public playWinAimation(): void {
		this._flyingWin =
			this.spineUtils.startSpineAnimationCreation('WILD_MULTIPLIER.json', 'EXIT_WIN', false)
				.position(this.layout.getAbsolutePosition('MULTIPLIER_METER'))
				.container(this._multiplierNumberLayer)
				.onComplete(() => {
					this._flyingWin = null;
					this.emit(WildMeterView.WIN_METER_EXIT_WIN_ANIMATION_FINISHED);
				})
				.createAndStart();

		this.emit(WildMeterView.WIN_METER_EXIT_WIN_ANIMATION_STARTED);
	}

	public restoreLastMultiplier(): void {
		this.restoreMultiplier(this._currentMultiplierValue);
	}

	public restoreMultiplier(toValue: number = 1): void {
		this.killMultiplier();

		if (!this._multiplier) {
			this._multiplier = this.spineUtils.startSpineAnimationCreation('WILD_MULTIPLIER.json', 'ENTER', false)
				.position(this.layout.getAbsolutePosition('MULTIPLIER_METER'))
				.container(this._multiplierNumberLayer)
				.chainIdleAnimation('IDLE')
				.createAndStart();
		}

		this.setMultiplierValue(toValue);
	}

	public addCoin(onCompleted: Function, targetCoinOnMeter: number): void {
		this._meter.play(`TRANSITION_0${targetCoinOnMeter - 1}_TO_0${targetCoinOnMeter}`, 0, false)
			.setTrackOnCompleteCallback(0, () => {
				if (targetCoinOnMeter >= 3) {
					this.timeUtils.createTimer(() => {
						this._meter.play('STAGE_00_IDLE', 0, true);
					}, 700);
				} else {
					this._meter.play(`STAGE_0${targetCoinOnMeter}_IDLE`, 0, true);
				}
				this.timeUtils.createTimer(() => {
					if (targetCoinOnMeter === 3) {
						if (this._isInFreeSpins) {
							this.playMultiplierTransitionIncremented();

							this.timeUtils.createTimer(() => {
								this.playMultiplierTransitionIncremented(() => {
									onCompleted && onCompleted();
								}, true);
							}, 670);

						} else {
							this.playMultiplierTransitionIncremented(() => {
								onCompleted && onCompleted();
							});
						}

						this.emit(WildMeterView.MULTIPLIER_UDPATE);

						this._currentCoinsOnMeter = 0;
					} else {
						onCompleted && onCompleted();
					}
				}, TimingConstants.ADD_COIN_COMPLETE_DELAY);
			});

		this.emit(WildMeterView.INDICATOR_LIT);
	}

	protected playMultiplierTransitionIncremented(onCompleted: Function = null, isSecond: boolean = false): void {
		this.playMultiplierTransition(this._currentMultiplierValue, this._currentMultiplierValue + 1, isSecond, () => {
			onCompleted && onCompleted();
		});

		this._currentMultiplierValue++;
	}

	public resetCoins(): void {
		if (this._currentCoinsOnMeter === 1) {
			this._meter.play('EXIT_01', 0, false)
				.setTrackOnCompleteCallback(0, () => {
					this._meter.play('STAGE_00_IDLE');
				});
		}

		if (this._currentCoinsOnMeter === 2) {
			this._meter.play('EXIT_02', 0, false)
				.setTrackOnCompleteCallback(0, () => {
				this._meter.play('STAGE_00_IDLE');
			});
		}

		this._currentCoinsOnMeter = 0;
		this._currentMultiplierValue = 1;
		this._currentCoinFlyingIndex = 0;

		this.cleanupCoins();
	}

	protected cleanupCoins(): void {
		for (const coin of this._wildCoinsOnReels) {
			if (coin.animation) {
				this.spineUtils.stopAndDestroyAnimation(coin.animation);
			}
		}

		this._wildCoinsOnReels = [];
	}

	public addWildCoinOntoReels(symbolPosition: SymbolPosition): void {
		const animation: SpineAnimation = this.spineUtils.startSpineAnimationCreation('WILD_SOUL.json', 'ENTER', false)
			.chainIdleAnimation('IDLE')
			.position(GameReelConstants.getGameSymbolCenterPosition(symbolPosition))
			.createAndStart();

		this._wildCoinsOnReels.push({animation, symbolPosition});

		this.emit(WildMeterView.ADD_WILD_COIN_TO_REELS);
	}

	public startSendingCoinsToWildMeter(): void {

		this.sortCoinsToBeSent();

		this.sendNextBatch(this._currentCoinFlyingIndex, this.onCoinAddingFinished.bind(this));
	}

	protected sortCoinsToBeSent(): void {
		const wildCoinsToSort: WildCoinOnReels[] = this._wildCoinsOnReels.splice(this._currentCoinFlyingIndex);
		const vesselPos: SymbolPosition = this.device.isMobile ? {x: 4, y: 6} : {x: -1, y: 3};

		wildCoinsToSort.sort((a: WildCoinOnReels, b: WildCoinOnReels): number => {
			const aDist: number = Math.hypot(vesselPos.x - a.symbolPosition.x, vesselPos.y - a.symbolPosition.y);
			const bDist: number = Math.hypot(vesselPos.x - b.symbolPosition.x, vesselPos.y - b.symbolPosition.y);

			return aDist - bDist;
		});

		this._wildCoinsOnReels = this._wildCoinsOnReels.concat(wildCoinsToSort);
	}

	protected onCoinAddingFinished(): void {
		if (this._currentCoinFlyingIndex >= this._wildCoinsOnReels.length) {
			this.timeUtils.createTimer(() => {
				this.emit(WildMeterView.WIN_METER_WILDS_ANIMATIONS_FINISHED);
			}, TimingConstants.SEND_COINS_COMPLETE_DELAY);
		} else {
			this.sendNextBatch(this._currentCoinFlyingIndex, this.onCoinAddingFinished.bind(this));
		}
	}

	protected sendNextBatch(coinIndex: number, onComplete: Function): void {

		const numCoinsToSend: number = Math.min(this._wildCoinsOnReels.length - coinIndex, 3 - this._currentCoinsOnMeter);

		let delay: number = 0;
		for (let i: number = 0; i < numCoinsToSend; i++) {
			this.timeUtils.createTimer(() => {
				const targetCoinOnMeter: number = this._currentCoinsOnMeter + 1;
				this.sendNextCoin(coinIndex + i, targetCoinOnMeter, (i === numCoinsToSend - 1) ? onComplete : null);
				this._currentCoinsOnMeter++;
			}, delay);

			delay += TimingConstants.SEND_COIN_STAGGER_DELAY;
		}

		this._currentCoinFlyingIndex += numCoinsToSend;

	}

	protected sendNextCoin(coinIndex: number, targetCoinOnMeter: number, onComplete: Function): void {

		const meterPosition: Position = this.layout.getAbsolutePosition(`MULTIPLIER_METER`);

		/* TODO Hookup to position in spine when that exists */
		if (this.device.isMobile) {
			meterPosition.y += 2;
			meterPosition.x += 91;
		} else {
			meterPosition.y -= 91;
			meterPosition.x += 4;
		}

		const currentCoin: WildCoinOnReels = this._wildCoinsOnReels[coinIndex];
		this.spineUtils.stopAndDestroyAnimation(currentCoin.animation);
		currentCoin.animation = null;
		currentCoin.animation = this.spineUtils.startSpineAnimationCreation('WILD_SOUL.json', 'BOUNCE', true)
			.position(GameReelConstants.getGameSymbolCenterPosition(currentCoin.symbolPosition))
			.createAndStart();
		const movingTween: Tween = new Tween(currentCoin.animation.container.position)
			.to({x: meterPosition.x, y: meterPosition.y}, 400).easing(Easing.Quintic.In)
			.onComplete(() => {
				this.spineUtils.stopAndDestroyAnimation(currentCoin.animation);
				coinIndex === 0 && this._currentMultiplierValue === 1 && this.playMultiplierIdleAnimation(this._currentMultiplierValue);
				currentCoin.animation = null;
				currentCoin.animation = this.spineUtils.startSpineAnimationCreation('WILD_SOUL.json', 'EXIT', false)
					.position(meterPosition)
					.createAndStart();

				this.addCoin(onComplete, targetCoinOnMeter);

				this.emit(WildMeterView.COIN_HIT_METER);
			})
			.start();
		this.updateManager.add(movingTween);

		this.emit(WildMeterView.SEND_COIN_TO_METER);
	}

	protected killMeter(): void {
		if (this._meter) {
			this.spineUtils.stopAndDestroyAnimation(this._meter);
			this._meter = null;
		}
	}

	protected killMultiplier(): void {
		if (this._multiplier) {
			this.spineUtils.stopAndDestroyAnimation(this._multiplier);
			this._multiplier = null;
		}
	}

	public clearMultiplier(): void {
		if (this._currentMultiplierValue > 1 && this._multiplierNumberLayer) {
			this.killMultiplier();
			this._multiplierNumberLayer.removeChildren();
		}
	}

	public resetMultiplierIdleAnimation(): void {
		this.playMultiplierIdleAnimation();
	}

	public fullMultiplierRestore(): void {
		if (this._currentMultiplierValue > 1) {
			this.clearMultiplier();
			this.restoreMultiplier();
		}
		this.resetCoins();
		this.resetMultiplierIdleAnimation();
	}

	public set isInFreeSpins(value: boolean) {
		this._isInFreeSpins = value;
	}

	public updateCoinsOnMeter(value: number): void {
		this._currentCoinsOnMeter = value;

		this.addCoin(null, this._currentCoinsOnMeter);
	}

	public set currentMultiplierValue(value: number) {
		this._currentMultiplierValue = value;
	}

	public setReelsContainerAlphaToZero(): void {
		this.container.alpha = 0;
	}

	public playReelContainerTransition(): void {
		const appearTween: Tween = new Tween(this.container)
			.to({alpha: 1.0}, BackgroundView.TRANSFER_TIME)
			.start();

		this.updateManager.add(appearTween);
	}
}



// WEBPACK FOOTER //
// ./src/wildmeter/WildMeterView.ts